import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

const MILESTONE_DATA = {
  first_activity:         { emoji: '🌟', labelKey: 'milestones.firstActivity',         messageKey: 'milestones.firstActivityMessage' },
  first_photo:            { emoji: '📸', labelKey: 'milestones.firstPhoto',            messageKey: 'milestones.firstPhotoMessage' },
  first_share:            { emoji: '💛', labelKey: 'milestones.firstShare',            messageKey: 'milestones.firstShareMessage' },
  first_reflection:       { emoji: '💭', labelKey: 'milestones.firstReflection',       messageKey: 'milestones.firstReflectionMessage' },
  five_activities:        { emoji: '5',  labelKey: 'milestones.fiveActivities',        messageKey: 'milestones.fiveActivitiesMessage', isBlackSymbol: true },
  ten_activities:         { emoji: '10', labelKey: 'milestones.tenActivities',         messageKey: 'milestones.tenActivitiesMessage', isBlackSymbol: true },
  twenty_five_activities: { emoji: '25', labelKey: 'milestones.twentyFiveActivities',  messageKey: 'milestones.twentyFiveActivitiesMessage', isBlackSymbol: true },
  first_elevation:        { emoji: '▲',  labelKey: 'milestones.firstElevation',        messageKey: 'milestones.firstElevationMessage', isBlackSymbol: true },
  multi_cause:            { emoji: '💚', labelKey: 'milestones.multiCause',            messageKey: 'milestones.multiCauseMessage' },
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn 0.2s ease-in-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Modal = styled.div`
  background-color: ${theme.colors.white};
  border-radius: 20px;
  padding: 40px 30px;
  max-width: 340px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  animation: slideUp 0.3s ease-in-out;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const Emoji = styled.div`
  font-size: ${props => props.$isNumber ? '48px' : '56px'};
  font-weight: ${props => props.$isNumber ? '800' : 'normal'};
  font-family: ${props => props.$isNumber ? theme.typography.headingFont : 'inherit'};
  line-height: 1;
  margin-bottom: 12px;
  color: ${props => props.$isBlack ? theme.colors.deepNavy : 'inherit'};
`;

const BadgeLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: ${theme.colors.brandYellow};
  margin-bottom: 8px;
`;

const Title = styled.h2`
  font-family: ${theme.typography.headingFont};
  font-size: 22px;
  font-weight: 700;
  color: ${theme.colors.deepNavy};
  margin: 0 0 12px;
`;

const Message = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: 15px;
  color: ${theme.colors.slateGray};
  line-height: 1.5;
  margin: 0 0 28px;
`;

const DismissButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.brandYellow} 0%, #FFC700 100%);
  color: ${theme.colors.deepNavy};
  border: none;
  border-radius: 12px;
  padding: 14px 32px;
  font-family: ${theme.typography.bodyFont};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 4px 8px rgba(255, 209, 0, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(255, 209, 0, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const MilestoneModal = ({ milestoneKey, onClose }) => {
  const { t } = useTranslation();
  const data = MILESTONE_DATA[milestoneKey];
  if (!data) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Emoji $isBlack={data.isBlackSymbol} $isNumber={!isNaN(data.emoji)}>{data.emoji}</Emoji>
        <BadgeLabel>{t('milestones.unlocked')}</BadgeLabel>
        <Title>{t(data.labelKey)}</Title>
        <Message>{t(data.messageKey)}</Message>
        <DismissButton onClick={onClose}>{t('milestones.keepGoing')}</DismissButton>
      </Modal>
    </Overlay>
  );
};

export default MilestoneModal;
